import { Calendar, Search } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Fragment } from "react";

import { NotificationsMenu } from "@/components/NotificationsMenu";
import { usePageTitleOverride } from "@/components/PageTitleContext";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { breadcrumbsFromPath } from "@/lib/pageTitles";

export function SiteHeader({ onSearch, formattedTime }) {
  const { pathname } = useLocation();
  const override = usePageTitleOverride();
  const crumbs = breadcrumbsFromPath(pathname);

  if (override && crumbs.length > 0) {
    crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], label: override };
  }

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mx-2 data-[orientation=vertical]:h-4"
        />
        <Breadcrumb className="min-w-0 flex-1">
          <BreadcrumbList className="flex-nowrap overflow-hidden">
            {crumbs.map((crumb, index) => {
              const isLast = index === crumbs.length - 1;
              return (
                <Fragment key={`${crumb.label}-${index}`}>
                  {index > 0 ? <BreadcrumbSeparator className="hidden sm:block" /> : null}
                  <BreadcrumbItem className={index < crumbs.length - 1 ? "hidden sm:inline-flex" : "min-w-0"}>
                    {isLast || !crumb.href ? (
                      <BreadcrumbPage className="truncate">{crumb.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onSearch}
            className="hidden h-8 w-48 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-xs text-muted-foreground transition hover:bg-secondary md:flex lg:w-64"
          >
            <Search className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 truncate text-left">Jump to a page…</span>
            <kbd className="rounded border border-border px-1 py-0.5 text-[10px]">
              Ctrl K
            </kbd>
          </button>

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onSearch}
            title="Search"
          >
            <Search className="h-4 w-4" />
          </Button>

          <NotificationsMenu />

          {formattedTime ? (
            <div className="hidden items-center gap-1.5 px-1 text-xs text-muted-foreground xl:flex">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formattedTime}</span>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
